import { UserContextState } from "@src/lib/contexts/UserContext";
import { useIntercom } from "react-use-intercom";
import { useCallback, useEffect, useLayoutEffect } from "react";
import { generateHMAC } from "@src/lib/utils/hmac";
import { useRouter } from "next/router";
import { useFeatureFlag } from "@src/features/app/hooks/useFeatureFlag";
import { Ff } from "@src/types.generated";
import type { IntercomProps } from "react-use-intercom";

type UseUpdateIntercomReturn = {
  show: () => void;
};

const useUpdateIntercom = (
  userContext?: UserContextState,
): UseUpdateIntercomReturn => {
  const {
    boot: bootIntercom,
    update: updateIntercom,
    hide: hideIntercom,
    show: showIntercom,
    trackEvent,
  } = useIntercom();
  const router = useRouter();
  const chatKnowledgeBaseEnabled = useFeatureFlag(Ff.ChatKnowledgeBase);

  useEffect(() => {
    if (
      userContext?.user &&
      process.env.NEXT_PUBLIC_VERCEL_ENV === "production" &&
      !userContext.isStaff
    ) {
      const payload = {
        userId: userContext.user.id.toString(),
        userHash: generateHMAC(
          process.env.NEXT_PUBLIC_INTERCOM_SECRET_KEY!,
          userContext.user.id.toString(),
        ),
        email: userContext.user.email || undefined,
        name: userContext.user.name!,
        company: {
          companyId: userContext.user.org!.id.toString(),
          name: userContext.user.org?.name,
        },
      };
      updateIntercom(payload);
      trackEvent("dashboard-loaded");
      hideIntercom();
    }
  }, [userContext]); // eslint-disable-line react-hooks/exhaustive-deps

  useLayoutEffect(() => {
    // Only hide the default launcher if the ChatKnowledgeBase feature flag is enabled
    // When the flag is enabled, users can access Intercom through the support dropdown
    // When disabled, keep the default launcher visible
    const hideDefaultLauncher =
      chatKnowledgeBaseEnabled || router.pathname.includes("ask-opus");
    updateIntercom({ hideDefaultLauncher });
  }, [updateIntercom, router.pathname, chatKnowledgeBaseEnabled]);

  const show = useCallback(() => {
    // Intercom only autoBoots in production builds, so custom entry points need
    // to boot manually before show() in local/dev environments.
    if (process.env.NODE_ENV !== "production") {
      const bootProps: IntercomProps = {};
      if (userContext?.user && !userContext.isStaff) {
        bootProps.userId = userContext.user.id.toString();
        bootProps.email = userContext.user.email || undefined;
        bootProps.name = userContext.user.name!;
        if (userContext.user.org) {
          bootProps.company = {
            companyId: userContext.user.org.id.toString(),
            name: userContext.user.org.name,
          };
        }
      }
      bootIntercom(bootProps);
    }
    showIntercom();
  }, [bootIntercom, showIntercom, userContext]);

  return {
    show,
  };
};

export default useUpdateIntercom;
